import { styled } from '@airwallex/theme';
import React, { HTMLAttributes } from 'react';
import { css } from '@emotion/react';
import { breakpoints } from '@airwallex/global';
import { white } from '@/components/common/palette';
import { useLargeScreen } from '@/hooks/useMediaQuery';

export const Container = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.white};
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    ${breakpoints.breakpoint.s} {
      padding: 0 ${theme.space.xs};
    }
  `
);

export const containerCss = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const desktopCss = css`
  background: white;
  padding: 0px;
  scrollbar-gutter: stable both-edges;
  &::before {
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    background: ${white};
    content: ' ';
    height: 100%;
    position: fixed;
    right: 0;
    top: 0;
    -webkit-transform-origin: right;
    -ms-transform-origin: right;
    transform-origin: right;
    width: 50%;
    box-shadow: 15px 0 30px 0 rgba(0, 0, 0, 0.18);
  }
`;

export const PayByLinkContainer: React.FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  return (
    <div
      css={css`
        display: flex;
        position: relative;
        justify-content: center;
        flex-direction: row;
        padding: 120px 0;
        min-height: 100vh;
        ${breakpoints.breakpoint.l} {
          padding: 56px 0;
          justify-content: center;
          align-items: center;
          flex-direction: column;
        }
      `}
      {...rest}
    >
      {children}
    </div>
  );
};

interface SplitScreenContainerProps extends HTMLAttributes<HTMLDivElement> {
  // Override the internal `useLargeScreen()` (≥1200px) check. Pass `!useMobile()`
  // (≥721px) when the page should switch to mobile at the same point as
  // shopify-lpm — the Xero invoice page does this so it stops collapsing into
  // mobile view between 720px–1199px.
  isLargeScreen?: boolean;
  // Override the hardcoded `white` background of the `desktopCss` main panel
  // (the left body column). The themed Xero invoice page passes
  // `semanticColors.background.secondary` — the order-info band surface, which
  // carries the merchant `colorBackground`'s generator-derived shade when
  // configured (see deriveLhsThemeOverride) — so the left body reads as a band
  // distinct from the payment column. The fixed-position `::before` right gutter
  // is intentionally left at its original white so the right payment section
  // keeps the original background (per design). Callers that pass undefined keep
  // the `#fff` panel.
  themedBackground?: string;
  // RFC-252 preview only: drop the mobile `containerCss` `min-height: 100vh` so
  // the embedded preview is content-sized. Without this the mobile preview is
  // viewport-tall, which the parent iframe over-reports (tall empty band + a
  // second scrollbar). Production checkout leaves this false to keep the
  // full-height live page (ADV-6289).
  contentSized?: boolean;
}

export const SplitScreenContainer: React.FC<SplitScreenContainerProps> = ({
  children,
  isLargeScreen: isLargeScreenProp,
  themedBackground,
  contentSized,
  ...rest
}) => {
  const detectedIsLargeScreen = useLargeScreen();
  const isLargeScreen = isLargeScreenProp ?? detectedIsLargeScreen;
  // Compose `desktopCss` with a background override when the preview page
  // requests one. Emotion merges later styles on top of earlier ones, so the
  // override targets `desktopCss`'s main `background` (the left body column)
  // while the rest of the layout (positioning, shadow) stays intact. The
  // fixed `::before` right gutter is deliberately not overridden so the right
  // payment section keeps its original white background (per design).
  const themedDesktopOverride =
    themedBackground &&
    css`
      background: ${themedBackground};
      /* Establish a BFC so the inner content's 120px top margin is contained and
         the merchant background fills the column from the very top instead of
         collapsing out and leaving a neutral band above the content. */
      display: flow-root;
    `;
  // In the content-sized preview, collapse `containerCss`'s mobile
  // `min-height: 100vh` so the page reports its real content height.
  const contentSizedOverride =
    contentSized &&
    css`
      min-height: 0;
    `;
  const baseCss = isLargeScreen ? desktopCss : containerCss;
  return (
    <div css={[baseCss, themedDesktopOverride, contentSizedOverride].filter(Boolean)} {...rest}>
      {children}
    </div>
  );
};
